import { PlusOutlined } from "@ant-design/icons";
import { Button, Checkbox, Col, Form, Input, Modal, Row, Space, Tooltip, Upload, message } from "antd";
import { getMediaUrl } from "config/env";
import { getBase64 } from "functions/functions";
import Loading from "layouts/Loading/Loading";
import OpenNotificationWithIcon from "layouts/notificationWithIcon/OpenNotificationWithIcon ";
import Axios from "middleware/axiosInstance";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

const { TextArea } = Input;

const normalizeTimeValue = (value) => {
  if (value === null || value === undefined || value === "") {
    return "";
  }

  const stringValue = String(value).trim();
  const match = stringValue.match(/^(\d{1,2}):(\d{2})/);

  if (match) {
    return `${match[1].padStart(2, "0")}:${match[2]}`;
  }

  return stringValue;
};

const isMissingMediaValue = (value) => {
  if (value === null || value === undefined) return true;

  const normalizedValue = String(value).trim().toLowerCase();
  return normalizedValue === "" || normalizedValue === "null" || normalizedValue === "undefined";
};

const getFirstMediaValue = (...values) =>
  values.find((value) => !isMissingMediaValue(value)) || "";

// Wrapper component to ensure TextArea receives form value properly
const AddressInputWithPrefix = ({ prefix, value, onChange, placeholder, rows = 3, maxLength = 500, direction = 'rtl' }) => {
  return (
    <Space.Compact style={{ width: '100%', direction }}>
      <Input
        value={prefix}
        disabled
        style={{
          width: '60px',
          flexShrink: 0
        }}
      />
      <TextArea
        value={value}
        onChange={onChange}
        rows={rows}
        maxLength={maxLength}
        placeholder={placeholder}
        style={{ flex: 1 }}
      />
    </Space.Compact>
  );
};

const AddAndEditLocation = () => {
  // start react router url params
  const UrlParams = useParams();
  const { locationId } = UrlParams;
  const isInEditMode = locationId ? true : false;
  // end react router url params
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [locationInfoForEdit, setLocationInfoForEdit] = useState(null);

  // upload image
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewImage, setPreviewImage] = useState("");
  const [previewTitle, setPreviewTitle] = useState("");
  const [fileList, setFileList] = useState([]);
  const [galleryFileList, setGalleryFileList] = useState([]);
  const [imageRemoved, setImageRemoved] = useState(false);
  const [galleryChanged, setGalleryChanged] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [uploadError, setUploadError] = useState("");
  const [imageFileList, setImageFileList] = useState([]);
  const [locationImageRemoved, setLocationImageRemoved] = useState(false);
  const [imageUploadError, setImageUploadError] = useState("");
  const handleImageChange = ({ fileList: newFileList }) => {
    setImageFileList(newFileList.slice(-1));
    if (newFileList.some((file) => file.originFileObj)) {
      setLocationImageRemoved(false);
    }
    setImageUploadError("");
  };
  const handleImageRemove = () => {
    setImageFileList([]);
    if (isInEditMode) setLocationImageRemoved(true);
    setImageUploadError("");
  };
  const validateImageFile = (file) => {
    if (!["image/jpeg", "image/png", "image/webp"].includes(file.type)) {
      const err = "Only JPG, JPEG, PNG, and WebP images are supported.";
      setImageUploadError(err);
      message.error(err);
      return Upload.LIST_IGNORE;
    }
    return false;
  };
  const handleCancel = () => setPreviewOpen(false);
  const handlePreview = async (file) => {
    if (!file.url && !file.preview) {
      file.preview = await getBase64(file.originFileObj);
    }
    setPreviewImage(getMediaUrl(file.url || file.preview));
    setPreviewOpen(true);
    setPreviewTitle(
      file.name || file.url.substring(file.url.lastIndexOf("/") + 1)
    );
  };
  const handleChange = ({ fileList: newFileList }) => {
    const nextFileList = newFileList.slice(-1);
    setFileList(nextFileList);

    // Ant Design calls onChange after onRemove. Only clear the removal flag
    // when a replacement file was actually selected; otherwise the update
    // request would omit the removed icon.
    if (nextFileList.some((file) => file.originFileObj)) {
      setImageRemoved(false);
    }

    setUploadError("");
  };
  const handleRemove = () => { setFileList([]); if (isInEditMode) setImageRemoved(true); };
  const handleGalleryChange = ({ fileList: newFileList }) => { setGalleryFileList(newFileList); setGalleryChanged(true); };
  const validateImage = (file) => { if (!["image/jpeg", "image/png", "image/webp"].includes(file.type)) { const err = "Only JPG, JPEG, PNG, and WebP images are supported."; setUploadError(err); message.error(err); return Upload.LIST_IGNORE; } return false; };
  const uploadButton = (
    <div>
      <PlusOutlined />
      <div
        style={{
          marginTop: 8,
        }}
      >
        Upload
      </div>
    </div>
  );
  // end of upload image

  const normalizeLocationPayload = (location) => {
    if (location.latlong !== undefined && location.latlong !== null && location.latlong.trim() !== '') {
      const coords = location.latlong.split(',');
      if (coords.length === 2) {
        location.latlong = `${coords[0].trim()},${coords[1].trim()}`;
      }
    } else {
      location.latlong = null;
    }

    location.work_start_time = normalizeTimeValue(location.work_start_time) || null;
    location.work_end_time = normalizeTimeValue(location.work_end_time) || null;

    // Remove separate latitude/longitude fields as backend expects latlong
    delete location.latitude;
    delete location.longitude;

    return location;
  };

  const buildFormData = async (location, edit = false) => {
    const data = new FormData();
    const payload = normalizeLocationPayload({ ...location });
    Object.entries(payload).forEach(([key, value]) => {
      if (
        ![
          "image",
          "icon",
          "icon_optimized",
          "icon_thumbnail",
          "image_optimized",
          "image_thumbnail",
          "gallery",
          "is_Central_office",
        ].includes(key) &&
        value !== undefined
      ) {
        data.append(key, value === null ? "" : value);
      }
    });

    data.append('is_Central_office', String(Boolean(location['is_Central_office'])));

    if (edit) data.append("_method", "PUT");

    if (fileList[0]?.originFileObj) {
      data.append("icon", fileList[0].originFileObj);
    } else if (imageRemoved) {
      data.append("icon", "null");
    }

    const selectedImageFile = imageFileList[0]?.originFileObj || (location.image instanceof File ? location.image : null);
    if (selectedImageFile instanceof File) {
      data.append("image", selectedImageFile);
    } else if (locationImageRemoved) {
      data.append("image", "null");
    }

    if (!edit || galleryChanged) {
      if (!galleryFileList.length) {
        data.append("gallery[]", "");
      } else if (edit) {
        // The update API replaces the whole gallery. Mixing existing string
        // values with uploaded files makes Laravel expose only the files, so
        // retained images disappear. Send one string-only array instead:
        // stored paths for existing images and data URLs for new images.
        const galleryItems = await Promise.all(
          galleryFileList.map((file) =>
            file.originFileObj
              ? getBase64(file.originFileObj)
              : file.galleryPath
          )
        );

        galleryItems.filter(Boolean).forEach((item) => {
          data.append("gallery[]", item);
        });
      } else {
        galleryFileList.forEach((file) => {
          if (file.originFileObj) data.append("gallery[]", file.originFileObj);
        });
      }
    }
    return data;
  };

  const SubmitNewLocation = async (location) => {
    setIsSubmitting(true);
    try {
      const data = await buildFormData(location);
      await Axios.post("/location", data);
      navigate("/locations");
      OpenNotificationWithIcon("ثبت موفق", "با موفقیت افزوده شد");
    } catch (err) {
      console.log(err);
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    if (isInEditMode) {
      getLocationInfoForEdit();
    }
  }, []);

  useEffect(() => {
    if (locationInfoForEdit) {
      // Prefer API-provided `latlong` string (normalize Persian comma),
      // otherwise combine `latitude` and `longitude` if available.
      const formData = { ...locationInfoForEdit };
      if (
        formData.latlong !== undefined &&
        formData.latlong !== null &&
        String(formData.latlong).trim() !== ""
      ) {
        formData.latlong = String(formData.latlong).replace("،", ",").trim();
      } else if (
        formData.latitude !== null &&
        formData.latitude !== undefined &&
        formData.longitude !== null &&
        formData.longitude !== undefined
      ) {
        formData.latlong = `${formData.latitude},${formData.longitude}`;
      } else {
        formData.latlong = "";
      }

      formData.slug = formData.slug ?? "";
      formData.work_start_time = normalizeTimeValue(formData.work_start_time);
      formData.work_end_time = normalizeTimeValue(formData.work_end_time);

      formData['is_Central_office'] = Boolean(
        locationInfoForEdit['is_Central_office'] === true ||
        locationInfoForEdit['is_Central_office'] === 'true' ||
        locationInfoForEdit['is_Central_office'] === 1
      );

      // Remove separate latitude/longitude from form data
      delete formData.latitude;
      delete formData.longitude;
      form.setFieldsValue(formData);
    }
  }, [locationInfoForEdit, form]);

  const getLocationInfoForEdit = () => {
    Axios.get(`/location/${locationId}`)
      .then((res) => {
        const data = res.data;
        setLocationInfoForEdit(data);
        // `icon` is the authoritative field. Some API responses retain an old
        // thumbnail path after the icon is cleared, or return the string
        // "null". In either case, leave the list empty so Upload shows its
        // add-image button instead of a broken/stale preview.
        const iconWasCleared =
          Object.prototype.hasOwnProperty.call(data, "icon") &&
          isMissingMediaValue(data.icon);
        const logo = iconWasCleared
          ? ""
          : getFirstMediaValue(data.icon_thumbnail, data.icon_optimized, data.icon);
        if (logo) {
          setFileList([{
            uid: "logo", name: "Current image", status: "done", url: getMediaUrl(logo), thumbUrl: getMediaUrl(logo)
          }]);
        } else {
          setFileList([]);
        }
        setImageRemoved(false);
        const imageWasCleared =
          Object.prototype.hasOwnProperty.call(data, "image") &&
          isMissingMediaValue(data.image);
        const locImage = imageWasCleared
          ? ""
          : getFirstMediaValue(data.image_thumbnail, data.image_optimized, data.image);
        if (locImage) {
          setImageFileList([{
            uid: "location-image",
            name: "Current image",
            status: "done",
            url: getMediaUrl(locImage),
            thumbUrl: getMediaUrl(locImage)
          }]);
        } else {
          setImageFileList([]);
        }
        setLocationImageRemoved(false);
        setGalleryFileList((data.gallery || []).slice().sort((a,b) => (a.order ?? 0)-(b.order ?? 0)).map((item, i) => { const path = item.image_thumbnail || item.image_optimized || item.image; return { uid: `gallery-${item.id ?? i}`, galleryId: item.id, galleryPath: item.image || path, name: `Gallery image ${i+1}`, status: "done", url: getMediaUrl(path), thumbUrl: getMediaUrl(path) }; }));
        setGalleryChanged(false);
      })
      .catch((err) => console.log(err));
  };

  const locationEditHandler = async (location) => {
    setIsSubmitting(true);
    try {
      const data = await buildFormData(location, true);
      await Axios.post(`/location/${locationId}`, data);
      navigate("/locations");
      OpenNotificationWithIcon("ویرایش موفق", "با موفقیت ویرایش شد");
    } catch (err) {
      console.log(err);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="customized-container">
      <div className="customized-wrapper">
        <h5 className="title-session">
          {isInEditMode ? `ویرایش شعبه ${locationInfoForEdit?.name_fa}` : "افزودن شعبه "}
        </h5>
        {isInEditMode && locationInfoForEdit === null ? (
          <Loading />
        ) : (
          <Form
            layout="vertical"
            initialValues={{
              'is_Central_office': false,
              phone2isMobile: false,
              is_hotel: false,
            }}
            form={form}
            autoComplete="off"
            scrollToFirstError
            labelCol={{ xs: 24 }}
            onFinish={isInEditMode ? locationEditHandler : SubmitNewLocation}
          >
            <Row gutter={8}>
              <Col xs={24} sm={12} md={8} xl={6}>
                <Form.Item
                  label="نام فارسی"
                  name="name_fa"
                  rules={[
                    {
                      required: true,
                      message: "نام فارسی الزامی می باشد",
                    },
                  ]}
                >
                  <Input placeholder="شعبه  مرکزی" />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} md={8} xl={6}>
                <Form.Item
                  label="نام انگلیسی"
                  name="name_en"
                  rules={[
                    {
                      required: true,
                      message: "نام انگلیسی الزامی می باشد",
                    },
                  ]}
                >
                  <Input placeholder="Main Branch" />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} md={8} xl={6}>
                <Form.Item
                  label="نام عربی"
                  name="name_ar"
                  rules={[
                    {
                      required: true,
                      message: "نام عربی الزامی می باشد",
                    },
                  ]}
                >
                  <Input placeholder="الفرع الرئيسي" />
                </Form.Item>
              </Col>

              <Col xs={24} sm={12} md={8} xl={6}>
                <Form.Item
                  label="اسلاگ"
                  name="slug"
                  extra="slug فقط برای نمایش است و قابل ویرایش نیست"
                >
                  <Input placeholder="branch-1" readOnly />
                </Form.Item>
              </Col>

              <Col xs={24} sm={12} md={8} xl={6}>
                <Form.Item
                  label="لینک موقعیت"
                  name="location_link"
                  extra="لینک گوگل‌مپ یا هر لینک موقعیتی که برای این شعبه ثبت شده است"
                >
                  <Input
                    placeholder="https://maps.google.com/?q=BamaDoreshahr"
                    style={{ direction: "ltr" }}
                  />
                </Form.Item>
              </Col>

              <Col xs={24} sm={12} md={8} xl={6}>
                <Form.Item label="شماره تماس ثابت" name="phone1"
                  rules={[
                    {
                      required: true,
                      message: "شماره تماس ثابت الزامی می باشد",
                    },
                  ]}
                >
                  <Input placeholder="02112345678" />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} md={8} xl={6}>
                <Form.Item label="شماره ثابت دوم/همراه" name="phone2">
                  <Input placeholder="09121234567" />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} md={8} xl={6}>
                <Form.Item
                  valuePropName="checked"
                  name="phone2isMobile"
                  label="شماره ثابت دوم/همراه"
                >
                  <Checkbox>شماره تماس دوم  تلفن همراه است</Checkbox>
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} md={8} xl={6}>
                <Form.Item
                  valuePropName="checked"
                  name="is_hotel"
                  label="نوع شعبه"
                >
                  <Checkbox>این شعبه هتل است</Checkbox>
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} md={8} xl={6}>
                <Form.Item
                  valuePropName="checked"
                  name="is_Central_office"
                  label="شعبه مرکزی"
                  tooltip="انتخاب این گزینه باعث عدم نمایش در لیست انتخاب شعبه، جهت مشاهده منو خواهد شد "
                >
                  <Checkbox>
                    <Tooltip placement="topLeft" title="انتخاب این گزینه باعث عدم نمایش در لیست انتخاب شعبه، جهت مشاهده منو خواهد شد ">
                      <span>شعبه مرکزی است</span>
                    </Tooltip>
                  </Checkbox>
                </Form.Item>
              </Col>

              <Col xs={24} sm={12} md={8} xl={6}>
                <Form.Item
                  label="ساعت شروع کار"
                  name="work_start_time"
                  rules={[
                    {
                      required: true,
                      message: "ساعت شروع کار الزامی می باشد",
                    },
                  ]}
                >
                  <Input
                    type="time"
                    step={60}
                    style={{ direction: "ltr" }}
                  />
                </Form.Item>
              </Col>

              <Col xs={24} sm={12} md={8} xl={6}>
                <Form.Item
                  label="ساعت پایان کار"
                  name="work_end_time"
                  rules={[
                    {
                      required: true,
                      message: "ساعت پایان کار الزامی می باشد",
                    },
                  ]}
                >
                  <Input
                    type="time"
                    step={60}
                    style={{ direction: "ltr" }}
                  />
                </Form.Item>
              </Col>

              <Col xs={24} sm={12} md={8} xl={6}>
                <Form.Item name="icon" label="آیکن شعبه">
                  <Upload
                    listType="picture-card"
                    fileList={fileList}
                    onPreview={handlePreview}
                    onChange={handleChange}
                    onRemove={handleRemove}
                    beforeUpload={validateImage}
                    accept=".jpg,.jpeg,.png,.webp,image/jpeg,image/png,image/webp"
                    multiple={false}
                    maxCount={1}
                  >
                    {fileList.length >= 1 ? null : uploadButton}
                  </Upload>
                </Form.Item>
                <Modal
                  open={previewOpen}
                  title={previewTitle}
                  footer={null}
                  onCancel={handleCancel}
                >
                  <img
                    alt="example"
                    style={{
                      width: "100%",
                    }}
                    src={previewImage}
                  />
                </Modal>
                {uploadError && <div role="alert" style={{ color: "#ff4d4f" }}>{uploadError}</div>}
              </Col>
              <Col xs={24} sm={12} md={8} xl={6}>
                <Form.Item name="image" label="تصویر شعبه">
                  <Upload
                    listType="picture-card"
                    fileList={imageFileList}
                    onPreview={handlePreview}
                    onChange={handleImageChange}
                    onRemove={handleImageRemove}
                    beforeUpload={validateImageFile}
                    accept=".jpg,.jpeg,.png,.webp,image/jpeg,image/png,image/webp"
                    multiple={false}
                    maxCount={1}
                  >
                    {imageFileList.length >= 1 ? null : uploadButton}
                  </Upload>
                  {imageUploadError && <div role="alert" style={{ color: "#ff4d4f" }}>{imageUploadError}</div>}
                </Form.Item>
              </Col>

              <Col xs={24}>
                <Form.Item label="گالری تصاویر شعبه">
                  <Upload listType="picture-card" fileList={galleryFileList} onPreview={handlePreview} onChange={handleGalleryChange} onRemove={() => setGalleryChanged(true)} beforeUpload={validateImage} accept=".jpg,.jpeg,.png,.webp,image/jpeg,image/png,image/webp" multiple>
                    {uploadButton}
                  </Upload>
                </Form.Item>
              </Col>

              <Col xs={24}>
                <Form.Item
                  label="آدرس فارسی"
                  name="address_fa"
                  extra="قم، به صورت خودکار به ابتدای آدرس اضافه می‌شود"
                  rules={[
                    {
                      required: true,
                      message: "آدرس فارسی الزامی می باشد",
                    },
                  ]}
                >
                  <AddressInputWithPrefix
                    prefix="قم،"
                    placeholder="خیابان ولیعصر، پلاک ۱۲۳"
                    rows={3}
                    maxLength={500}
                  />
                </Form.Item>
              </Col>
              <Col xs={24}>
                <Form.Item
                  label="آدرس انگلیسی"
                  name="address_en"

                  rules={[
                    {
                      required: true,
                      message: "آدرس انگلیسی الزامی می باشد",
                    },
                  ]}
                >
                  <AddressInputWithPrefix
                    prefix="Qom,"
                    placeholder="Valiasr Street, No. 123"
                    rows={3}
                    maxLength={500}
                    direction="ltr"
                  />
                </Form.Item>
              </Col>
              <Col xs={24}>
                <Form.Item
                  label="آدرس عربی"
                  name="address_ar"
                  rules={[
                    {
                      required: true,
                      message: "آدرس عربی الزامی می باشد",
                    },
                  ]}
                >
                  <AddressInputWithPrefix
                    prefix="قم،"
                    placeholder="شارع ولي عصر، رقم ۱۲۳"
                    rows={3}
                    maxLength={500}
                  />
                </Form.Item>
              </Col>

              <Col xs={24}>
                <Form.Item
                  label="توضیحات فارسی"
                  name="description_fa"
                >
                  <TextArea
                    placeholder="توضیحات مربوط به شعبه به فارسی"
                    rows={4}
                    maxLength={1000}
                    showCount
                  />
                </Form.Item>
              </Col>
              <Col xs={24}>
                <Form.Item
                  label="توضیحات انگلیسی"
                  name="description_en"
                >
                  <TextArea
                    placeholder="Location description in English"
                    rows={4}
                    maxLength={1000}
                    showCount
                    style={{ direction: 'ltr' }}
                  />
                </Form.Item>
              </Col>
              <Col xs={24}>
                <Form.Item
                  label="توضیحات عربی"
                  name="description_ar"
                >
                  <TextArea
                    placeholder="وصف الفرع بالعربية"
                    rows={4}
                    maxLength={1000}
                    showCount
                  />
                </Form.Item>
              </Col>


              <Col xs={24} sm={12} md={8} xl={6}>
                <Form.Item
                  label="مختصات جغرافیایی (Latitude, Longitude)"
                  name="latlong"
                  extra="فرمت: عرض جغرافیایی, طول جغرافیایی (مثال: 35.6892, 51.3890)"
                  rules={[
                    {
                      validator: (_, value) => {
                        if (!value?.trim()) return Promise.resolve();

                        const normalized = value.replace('،', ',');
                        const coords = normalized.split(',');

                        if (coords.length !== 2) {
                          return Promise.reject(new Error('فرمت صحیح نیست. مثال: 35.6892,51.3890'));
                        }

                        const lat = parseFloat(coords[0].trim());
                        const lng = parseFloat(coords[1].trim());

                        if (isNaN(lat) || lat < -90 || lat > 90) {
                          return Promise.reject(new Error('عرض جغرافیایی باید بین -90 تا 90 باشد'));
                        }

                        if (isNaN(lng) || lng < -180 || lng > 180) {
                          return Promise.reject(new Error('طول جغرافیایی باید بین -180 تا 180 باشد'));
                        }

                        return Promise.resolve();
                      },
                    },
                  ]}
                >
                  <Input
                    placeholder="35.6892, 51.3890"
                    style={{ direction: 'ltr' }}
                  />
                </Form.Item>
              </Col>

              <Col xs={24}>
                <Button htmlType="submit" type="primary" loading={isSubmitting}>
                  {isInEditMode ? "ثبت تغییرات" : "افزودن"}
                </Button>
                <Button
                  className="me-3"
                  onClick={() => navigate("/locations")}
                  style={{ marginRight: 8 }}
                >
                  بازگشت
                </Button>
              </Col>
            </Row>
          </Form>
        )}
      </div>
    </div>
  );
};
export default AddAndEditLocation;
